import { useEffect, useMemo, useState } from 'react';
import useEditFormScroll, { editHighlightStyle } from '../hooks/useEditFormScroll';
import BanquetHelpPanel from './BanquetHelpPanel';

function BanquetSlotMaster({ token, selectedProperty, selectedOutletId = '', showHelp = false, onOpenFaq }) {
  const chainId = String(selectedProperty?.chain_id || '').trim();
  const propertyId = String(selectedProperty?.id || '').trim();
  const outletId = String(selectedOutletId || '').trim();
  const headers = useMemo(
    () => ({
      Authorization: `Bearer ${token}`,
      'x-chain-id': chainId,
      'x-property-id': propertyId,
      ...(outletId ? { 'x-outlet-id': outletId } : {})
    }),
    [token, chainId, propertyId, outletId]
  );

  const { formRef, editHighlight, scrollToForm } = useEditFormScroll();
  const [rows, setRows] = useState([]);
  const [editingId, setEditingId] = useState('');
  const [error, setError] = useState('');
  const [form, setForm] = useState({
    slot_name: '',
    slot_code: '',
    start_time: '',
    end_time: '',
    is_active: true
  });

  const parseJson = async (res) => {
    const text = await res.text();
    try {
      const json = text ? JSON.parse(text) : {};
      return { data: json, error: json?.error || '' };
    } catch {
      return { data: null, error: text || '' };
    }
  };

  const loadRows = async () => {
    const res = await fetch('/api/v1/banquet/event-slots?all=1', { headers });
    const { data, error: apiError } = await parseJson(res);
    if (!res.ok) return setError(apiError || 'Failed to load slot master');
    setRows(Array.isArray(data) ? data : []);
  };

  useEffect(() => {
    if (!chainId || !propertyId) return;
    loadRows().catch((err) => setError(err?.message || 'Failed to load slot master'));
  }, [chainId, propertyId, outletId]);

  const resetForm = () => {
    setEditingId('');
    setForm({ slot_name: '', slot_code: '', start_time: '', end_time: '', is_active: true });
  };

  const save = async () => {
    setError('');
    if (!form.slot_name) return setError('Slot name is required');
    const url = editingId ? `/api/v1/banquet/event-slots/${editingId}` : '/api/v1/banquet/event-slots';
    const method = editingId ? 'PUT' : 'POST';
    const res = await fetch(url, {
      method,
      headers: { ...headers, 'Content-Type': 'application/json' },
      body: JSON.stringify({
        slot_name: form.slot_name,
        slot_code: form.slot_code || form.slot_name,
        start_time: form.start_time || null,
        end_time: form.end_time || null,
        is_active: form.is_active !== false
      })
    });
    const { error: apiError } = await parseJson(res);
    if (!res.ok) return setError(apiError || 'Save failed');
    resetForm();
    loadRows();
  };

  const edit = (r) => {
    setEditingId(String(r.slot_id));
    setForm({
      slot_name: r.slot_name || '',
      slot_code: r.slot_code || '',
      start_time: String(r.start_time || '').slice(0, 5),
      end_time: String(r.end_time || '').slice(0, 5),
      is_active: r.is_active !== false
    });
    scrollToForm();
  };

  const deactivate = async (slotId) => {
    setError('');
    const res = await fetch(`/api/v1/banquet/event-slots/${slotId}`, { method: 'DELETE', headers });
    const { error: apiError } = await parseJson(res);
    if (!res.ok) return setError(apiError || 'Deactivate failed');
    loadRows();
  };

  return (
    <div className="page-shell">
      <h2 style={{ color: 'var(--theme-primary)' }}>Banquet - Slot Master</h2>
      <BanquetHelpPanel visible={showHelp} onOpenFaq={onOpenFaq} />
      {error ? <div className="error-banner">{error}</div> : null}

      <div className="filter-bar" ref={formRef} style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(180px,1fr))', gap: '0.6rem', ...editHighlightStyle(editHighlight) }}>
        <input placeholder="Slot name (eg: Breakfast)" value={form.slot_name} onChange={(e) => setForm({ ...form, slot_name: e.target.value })} />
        <input placeholder="Code (optional, eg: BREAKFAST)" value={form.slot_code} onChange={(e) => setForm({ ...form, slot_code: e.target.value })} />
        <input type="time" value={form.start_time} onChange={(e) => setForm({ ...form, start_time: e.target.value })} />
        <input type="time" value={form.end_time} onChange={(e) => setForm({ ...form, end_time: e.target.value })} />
        <label><input type="checkbox" checked={form.is_active} onChange={(e) => setForm({ ...form, is_active: e.target.checked })} /> Active</label>
        <button onClick={save}>{editingId ? 'Update Slot' : 'Add Slot'}</button>
        {editingId ? <button onClick={resetForm}>Cancel</button> : null}
      </div>

      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', background: '#fff', border: '1px solid var(--theme-border)' }}>
          <thead>
            <tr style={{ background: '#e6f0fa' }}>
              <th style={{ textAlign: 'left' }}>Name</th>
              <th style={{ textAlign: 'left' }}>Code</th>
              <th style={{ textAlign: 'left' }}>Time</th>
              <th style={{ textAlign: 'left' }}>Active</th>
              <th style={{ textAlign: 'left' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.slot_id}>
                <td>{r.slot_name}</td>
                <td>{r.slot_code}</td>
                <td>{r.start_time && r.end_time ? `${String(r.start_time).slice(0, 5)} - ${String(r.end_time).slice(0, 5)}` : 'Full Day / Flexible'}</td>
                <td>{r.is_active ? 'Yes' : 'No'}</td>
                <td>
                  <button onClick={() => edit(r)}>Edit</button>
                  <button onClick={() => deactivate(r.slot_id)} style={{ marginLeft: '0.4rem' }}>Deactivate</button>
                </td>
              </tr>
            ))}
            {!rows.length ? <tr><td colSpan={5}>No slots found.</td></tr> : null}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default BanquetSlotMaster;
